import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { launchApi, useAdminRealtime } from "@/lib/admin-api";
import type {
  LaunchTaskListParams,
  UpdateLaunchTaskInput,
  CreateLaunchTaskInput,
} from "@/lib/admin-api";

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

const KEYS = {
  all: ["admin-launch"] as const,
  tasks: (params: LaunchTaskListParams) =>
    ["admin-launch", "tasks", params] as const,
} as const;

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/** Fetch the launch task list, filtered by optional params. */
export function useLaunchTasks(params: LaunchTaskListParams) {
  useAdminRealtime("launch_tasks", KEYS.all);
  return useQuery({
    queryKey: KEYS.tasks(params),
    queryFn: () => launchApi.listTasks(params),
    staleTime: 10_000,
  });
}

/** Update a single launch task (status, assignee, etc.) and invalidate cache. */
export function useUpdateLaunchTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      input,
    }: {
      readonly id: string;
      readonly input: UpdateLaunchTaskInput;
    }) => launchApi.updateTask(id, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.all });
    },
  });
}

/** Create a new launch task and invalidate cache. */
export function useCreateLaunchTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateLaunchTaskInput) => launchApi.createTask(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.all });
    },
  });
}
